import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  ApiService,
  AuthService,
  DocumentService,
  handleClassCondition,
  PageService,
  PageType,
  PostService,
  PostType,
  SettingsService,
  StorageService,
} from '@gallaghercp/core-frontend';
import { ClientService } from '../../services/client.service';
import { DomSanitizer } from '@angular/platform-browser';
import { APP_CONFIG } from '../../app.config';
import { Observable, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { CampusService } from '@core-app/services/campus.service';

@Component({
  selector: 'core-main',
  templateUrl: './main.component.html',
  standalone: false,
})
export class MainComponent implements OnInit, AfterViewInit, OnDestroy {
  protected router = inject(Router);
  protected auth = inject(AuthService);
  protected client = inject(ClientService);
  protected campus = inject(CampusService);
  protected settings = inject(SettingsService);
  protected pages = inject(PageService);
  protected posts = inject(PostService);
  protected api = inject(ApiService);
  protected storage = inject(StorageService);
  protected sanitizer = inject(DomSanitizer);
  protected changeDetectorRef = inject(ChangeDetectorRef);
  protected documentService = inject(DocumentService);

  containerClass?: string;
  page?: any;
  post: any;
  category?: string | boolean;
  delayedLoaded = true;
  gtm: any;

  isTopNav$?: Observable<boolean>;
  isTopNavTransparent?: boolean;
  notificationsEnabled?: boolean;
  heroImagesEnabled?: boolean;
  hasBlurEffect?: boolean;
  isChatEnabled: boolean = APP_CONFIG.chatEnabled ?? false;
  isDemoMode: boolean = APP_CONFIG.demoMode ?? false;

  logoUrl!: string;
  protected logoInvertedUrl!: string;

  currentUrl = [APP_CONFIG.frontend, this.router.url].join('');
  today = new Date();

  subscriptions: Subscription = new Subscription();
  private initialCampusLoad = true;

  ngOnInit(): void {
    this.isTopNav$ = this.settings.isTopNavigation();

    this.subscriptions.add(
      this.settings.isTransparentNavigation().subscribe((state: any) => {
        this.isTopNavTransparent = state;
      }),
    );

    this.subscriptions.add(
      this.settings.isBlurredBackground().subscribe((state: any) => {
        this.hasBlurEffect = state;
      }),
    );

    this.subscriptions.add(
      this.settings.showNotifications().subscribe((state: any) => {
        this.notificationsEnabled = state;
      }),
    );

    this.subscriptions.add(
      this.settings.getAuthenticationType().subscribe((authenticationType: any) => {
        if (!this.auth.isAuthenticated() && authenticationType !== 'public') {
          this.router.navigate(['/login']);
        }
      }),
    );

    this.subscriptions.add(
      this.settings.showHeroImages().subscribe((state: any) => {
        this.heroImagesEnabled = state;
      }),
    );

    this.subscriptions.add(
      this.settings.getInvertedLogo().subscribe((logoUrl: any) => {
        this.logoInvertedUrl = logoUrl;
        this.updateLogoUrl();
      }),
    );

    if (APP_CONFIG.gtm) {
      this.gtm = this.sanitizer.bypassSecurityTrustResourceUrl(
        'https://www.googletagmanager.com/ns.html?id=' + APP_CONFIG.gtm,
      );
    }

    this.subscriptions.add(
      this.pages.currentPage$!.subscribe((page: PageType) => {
        if (page) {
          this.delayedLoaded = false;
          this.page = page as PageType;
          this.post = false;
          this.posts.currentPost$!.next(false);
          this.category =
            this.page.categoryNames && this.page.categoryNames[0]
              ? this.page.categoryNames[0].toLowerCase().replace(new RegExp(' ', 'g'), '-')
              : false;
          this.changeDetectorRef.detectChanges();
          this.setContainerClass();
          setTimeout(() => {
            this.delayedLoaded = true;
          }, 300);
        }
      }),
    );

    this.subscriptions.add(
      this.posts.currentPost$!.subscribe((post: PostType) => {
        if (post) {
          this.delayedLoaded = false;
          this.post = post;
          this.page = undefined;
          this.pages.currentPage$!.next(false);
          this.category =
            this.post.categoryNames && this.post.categoryNames[0]
              ? this.post.categoryNames[0].toLowerCase().replace(new RegExp(' ', 'g'), '-')
              : false;
          this.changeDetectorRef.detectChanges();
          this.setContainerClass();
          setTimeout(() => {
            this.delayedLoaded = true;
          }, 300);
        }
      }),
    );

    // FIXED: Only redirect on actual campus changes, not initial load
    this.subscriptions.add(
      this.campus.selectedCampus$.subscribe(() => {
        if (this.initialCampusLoad) {
          this.initialCampusLoad = false;
        } else {
          this.router.navigate(['/']);
        }
      }),
    );
    this.campus.checkCampusCookie();

    // Remove target="_blank" from all links
    // All request of client, all links require links to open in the same tab for accessibility reasons.
    setTimeout(() => {
      Array.from(document.querySelectorAll('a[target="_blank"]')).forEach(link => link.removeAttribute('target'));
    }, 1000);
  }

  protected setContainerClass() {
    // this.checkContentKey();
    const body: Element | null = this.documentService.querySelector('body');

    if (body) {
      switch (this.router.url) {
        case '/':
        case '/1':
        case '/#main-content':
        case '/default':
          this.containerClass = 'container-fluid p-0';
          handleClassCondition(true, 'is-home', body as HTMLElement);
          this.changeDetectorRef.detectChanges();
          break;
        default:
          this.containerClass = this.isTopNavTransparent ? 'container-fluid p-0' : 'container-fluid';
          handleClassCondition(false, 'is-home', body as HTMLElement);
          this.changeDetectorRef.detectChanges();
          break;
      }
    }
  }

  getBackgroundImage(): string {
    if (this.page && this.page.meta && this.page.meta.icon) {
      return APP_CONFIG.frontend + String(this.page.meta.icon);
    } else if (this.post && this.post.data.image) {
      return APP_CONFIG.frontend + String(this.post.data.image);
    }
    return '';
  }

  updateLogoUrl(): void {
    this.logoUrl = this.logoInvertedUrl ? this.logoInvertedUrl : '';
  }

  ngAfterViewInit() {
    this.setContainerClass();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
